import { useNavigate } from "react-router";
import { Tooltip } from "@mui/material";
import type { QueryObserverResult } from "@tanstack/react-query";
import TimeTransformer from "src/business-components/general/TimeTransformer/TimeTransformer";
import TaskRunSelector from "src/business-components/input/TaskRunSelector/TaskRunSelector";
import { ALL_TASKS } from "src/business-components/input/TaskSelector/TaskSelector.constants";
import RunStatusBar from "src/business-components/RunsStatusBars/components/RunStatusBar/RunStatusBar";
import { CopyText } from "src/components/features/small/CopyText/CopyText";
import PopperDropdown from "src/components/general/PopperDropdown/PopperDropdown";
import { withInlineAutocomplete } from "src/components/input/withInlineAutocomplete/withInlineAutocomplete";
import { useBuildLink } from "src/hooks/useBuildLink";
import { useQueryMinimalTasksRuns } from "src/queries/tasks/tasks.query";
import type { Task } from "src/queries/tasks/tasks.types";
import { useGlobalLocalContext } from "src/storage/localStorage";
import type { TaskPath, TaskRunPath } from "src/utils/appRoutes/appRoutes.types";
import { TaskIcon } from "@ui-icons";
import BreadcrumbLink from "../BreadcrumbLink/BreadcrumbLink";
import BreadcrumbPopperArrow from "../BreadcrumbPopperArrow/BreadcrumbPopperArrow";

const InlineTaskSelector = withInlineAutocomplete(TaskRunSelector);

interface BreadcrumbTaskLinkProps {
  taskQuery: QueryObserverResult<Task>;
  env: string;
  app_name: string;
  app_pit: string;
  task_id?: string;
}

const BreadcrumbTaskRunsLink = ({
  taskQuery,
  env,
  app_name,
  app_pit,
  task_id,
}: BreadcrumbTaskLinkProps) => {
  const { isDebugMode } = useGlobalLocalContext();
  const tasksQuery = useQueryMinimalTasksRuns({
    params: { app_pit },
  });
  const navigate = useNavigate();
  const buildLink = useBuildLink();

  const to =
    buildLink({
      app_name,
      app_pit,
      task_name: taskQuery.data?.task_name,
      search: window.location.search,
    } as unknown as TaskPath) ?? `/envs/${env}/apps/${app_name}/${app_pit}`;

  const arrowMode = !!task_id;

  return (
    <PopperDropdown
      slotProps={{
        popper: {
          disablePortal: true,
        },
      }}
      content={({ hide }) => (
        <InlineTaskSelector
          autoFocus
          isLoading={tasksQuery.isPending}
          task={taskQuery.data}
          tasks={tasksQuery.data}
          slotProps={{
            renderOption: {
              onClick: (_e, option) => {
                const toRun: TaskRunPath = {
                  app_name,
                  app_pit,
                  task_id: option.task_id,
                  search: window.location.search,
                };
                const runUrl = buildLink(toRun);
                if (runUrl) {
                  hide();
                  void navigate(runUrl);
                }
              },
            },
          }}
        />
      )}
    >
      {({ isOpen, anchorRef, handleOnMouseDownCapture, toggleAndPrevent }) => (
        <BreadcrumbLink
          ref={anchorRef}
          data-test-id="breadcrumb-task-run"
          className="text-xs"
          isLoading={!!task_id && taskQuery.isPending}
          to={to}
          startAdornment={TaskIcon}
          onClick={arrowMode ? undefined : toggleAndPrevent}
          onMouseDownCapture={arrowMode ? undefined : handleOnMouseDownCapture}
          endAdornment={
            <BreadcrumbPopperArrow
              isOpen={isOpen}
              onClick={toggleAndPrevent}
              onMouseDownCapture={handleOnMouseDownCapture}
            />
          }
          tooltip={
            isOpen ? undefined : task_id ? (
              <div className="flex flex-col gap-1">
                <span>{taskQuery.data?.task_name}</span>
                <TimeTransformer ts={taskQuery.data?.start_time} />
              </div>
            ) : null
          }
          TooltipComponent={Tooltip}
        >
          {task_id ? (
            <>
              <div className="inline-flex items-center gap-2 min-w-0">
                <div className="w-fit">
                  <RunStatusBar
                    status={taskQuery.data?.status}
                    withTooltip
                    className="w-1! h-[16px]!"
                  />
                </div>
                <div className="truncate">{taskQuery.data?.task_name ?? ""}</div>
              </div>

              {isDebugMode && (
                <CopyText text={taskQuery.data?.task_name ?? ""} hideText />
              )}
            </>
          ) : (
            ALL_TASKS
          )}
        </BreadcrumbLink>
      )}
    </PopperDropdown>
  );
};

export default BreadcrumbTaskRunsLink;
